import { type CSSProperties, type NestedCSSProperties } from 'typestyle/lib/types';
import StyleConstants from '../../core/styles.constants';
import DashboardTheme from '../statistics/dashboard/dashboard-theme';
import { tint } from '../../core/admin-surface.styles';

// Layout and spacing follow the approved office mockup; colours come from the app's palette
// (StyleConstants), which is what the mockup's navy/blue/amber trio maps onto.

export const formGrid = (isMobile: boolean): CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) minmax(260px, 300px)',
  gap: 24,
  alignItems: 'start',
});

export const sectionCard: CSSProperties = {
  background: StyleConstants.WHITE_COLOR,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 14,
  padding: 24,
  marginBottom: 24,
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const sectionTitle: CSSProperties = {
  fontFamily: StyleConstants.HEADER_FONT_FAMILY,
  fontSize: 18,
  fontWeight: 600,
  letterSpacing: '-0.01em',
  color: StyleConstants.DARK_BLUE_COLOR,
  margin: 0,
  display: 'block',
};

export const sectionDescription: CSSProperties = {
  display: 'block',
  color: DashboardTheme.gray700,
  fontSize: 13.5,
  lineHeight: 1.5,
  margin: '6px 0 0',
  maxWidth: 560,
};

export const sectionContent: CSSProperties = {
  display: 'grid',
  gap: 20,
  marginTop: 24,
};

export const fieldRow = (isMobile: boolean): CSSProperties => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  gap: 16,
  maxWidth: 560,
});

export const fieldColumn: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

export const fieldLabel: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13.5,
  fontWeight: 600,
  color: StyleConstants.DARK_BLUE_COLOR,
  marginBottom: 6,
};

export const fieldHint: CSSProperties = {
  display: 'block',
  fontSize: 12,
  color: DashboardTheme.gray500,
  lineHeight: 1.45,
  marginTop: 6,
};

export const fieldError: CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: StyleConstants.PINK_COLOR,
  marginTop: 6,
};

export const textInput: NestedCSSProperties = {
  width: '100%',
  maxWidth: 480,
  padding: '9px 12px',
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 13.5,
  color: StyleConstants.DARK_BLUE_COLOR,
  border: `1px solid ${DashboardTheme.gray300}`,
  borderRadius: 10,
  background: StyleConstants.WHITE_COLOR,
  $nest: {
    '&:focus': {
      outline: `2px solid ${StyleConstants.MEDIUM_BLUE_COLOR}`,
      outlineOffset: -1,
      borderColor: StyleConstants.MEDIUM_BLUE_COLOR,
    },
    '&:disabled': {
      background: DashboardTheme.gray50,
      color: DashboardTheme.gray500,
    },
  },
};

// Input with a unit suffix ("%", "days") pinned inside the field.
export const adorned = (width?: number): CSSProperties => ({
  position: 'relative',
  maxWidth: width ?? 480,
});

export const adornedInput: NestedCSSProperties = {
  ...textInput,
  maxWidth: '100%',
  paddingRight: 56,
};

export const adornmentUnit: CSSProperties = {
  position: 'absolute',
  right: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: 12.5,
  color: DashboardTheme.gray500,
  pointerEvents: 'none',
};

export const divider: CSSProperties = {
  height: 1,
  background: DashboardTheme.gray200,
};

// A switch and its explanation, stacked in a list with hairline separators.
export const switchRow = (isFirst: boolean): CSSProperties => ({
  display: 'flex',
  gap: 16,
  alignItems: 'flex-start',
  padding: '16px 0',
  borderTop: isFirst ? undefined : `1px solid ${DashboardTheme.gray200}`,
});

export const switchRowBody: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

export const switchRowName: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  fontSize: 13.5,
  fontWeight: 600,
  color: StyleConstants.DARK_BLUE_COLOR,
};

export const switchRowDescription: CSSProperties = {
  display: 'block',
  color: DashboardTheme.gray700,
  fontSize: 13,
  lineHeight: 1.5,
  marginTop: 2,
  maxWidth: 520,
};

export const switchRowControl: CSSProperties = {
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
};

export const chip: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '3px 10px',
  borderRadius: 12,
  background: DashboardTheme.gray100,
  color: StyleConstants.DARK_BLUE_COLOR,
  fontSize: 11.5,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

export const cautionChip: CSSProperties = {
  ...chip,
  background: tint(StyleConstants.ORANGE_COLOR, 0.22),
  color: '#8A5A06',
};

export const sliderRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 20,
  maxWidth: 480,
};

export const slider: NestedCSSProperties = {
  flex: 1,
  appearance: 'none',
  '-webkit-appearance': 'none',
  height: 4,
  borderRadius: 2,
  background: DashboardTheme.gray200,
  cursor: 'pointer',
  $nest: {
    // typestyle's NestedCSSProperties has no vendor-prefixed appearance; the base rule above
    // already sets -webkit-appearance for the track, and the thumb only needs the shape.
    '&::-webkit-slider-thumb': {
      appearance: 'none',
      width: 18,
      height: 18,
      borderRadius: '50%',
      background: StyleConstants.DARK_BLUE_COLOR,
      cursor: 'pointer',
      boxShadow: `0 0 0 0 ${tint(StyleConstants.MEDIUM_BLUE_COLOR, 0.22)}`,
      transition: 'box-shadow .15s',
    },
    '&:hover::-webkit-slider-thumb': {
      boxShadow: `0 0 0 8px ${tint(StyleConstants.MEDIUM_BLUE_COLOR, 0.22)}`,
    },
    '&::-moz-range-thumb': {
      width: 18,
      height: 18,
      border: 0,
      borderRadius: '50%',
      background: StyleConstants.DARK_BLUE_COLOR,
      cursor: 'pointer',
    },
    '&:disabled': {
      cursor: 'default',
    },
  },
};

export const sliderValue: CSSProperties = {
  width: 108,
  flex: '0 0 auto',
};

// Capacity band: the share of seats open for booking, with the quiet/busy threshold range on top.
export const band: CSSProperties = {
  position: 'relative',
  height: 10,
  borderRadius: 5,
  background: DashboardTheme.gray100,
  overflow: 'hidden',
  marginTop: 16,
};

export const bandTarget = (widthPercentage: number): CSSProperties => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  width: `${widthPercentage}%`,
  background: tint(StyleConstants.DARK_BLUE_COLOR, 0.28),
});

export const bandRange = (leftPercentage: number, widthPercentage: number): CSSProperties => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: `${leftPercentage}%`,
  width: `${widthPercentage}%`,
  background: StyleConstants.MEDIUM_BLUE_COLOR,
});

export const bandBusy = (leftPercentage: number, widthPercentage: number): CSSProperties => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: `${leftPercentage}%`,
  width: `${widthPercentage}%`,
  background: tint(StyleConstants.ORANGE_COLOR, 0.55),
});

export const bandLegend: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  marginTop: 8,
  fontSize: 11.5,
  color: DashboardTheme.gray500,
};

export const callout: CSSProperties = {
  display: 'flex',
  gap: 12,
  alignItems: 'center',
  padding: '14px 16px',
  borderRadius: 10,
  background: tint(StyleConstants.DARK_BLUE_COLOR, 0.06),
  color: StyleConstants.DARK_BLUE_COLOR,
  fontSize: 13,
  lineHeight: 1.5,
};

export const calloutInfo: CSSProperties = {
  ...callout,
  background: '#EAF3FC',
  color: '#1B4E7F',
  marginTop: 16,
};

export const calloutIcon: CSSProperties = {
  flex: '0 0 auto',
  color: StyleConstants.MEDIUM_BLUE_COLOR,
  lineHeight: 0,
};

export const rail = (isMobile: boolean): CSSProperties => ({
  background: StyleConstants.WHITE_COLOR,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 14,
  padding: 20,
  fontFamily: DashboardTheme.bodyFontFamily,
  position: isMobile ? undefined : 'sticky',
  top: isMobile ? undefined : 24,
});

export const railTitle: CSSProperties = {
  display: 'block',
  fontSize: 13.5,
  fontWeight: 600,
  color: StyleConstants.DARK_BLUE_COLOR,
  margin: '0 0 16px',
};

export const railBlock = (isFirst: boolean): CSSProperties => ({
  padding: isFirst ? '0 0 16px' : '16px 0',
  borderTop: isFirst ? undefined : `1px solid ${DashboardTheme.gray200}`,
});

export const railKey: CSSProperties = {
  display: 'block',
  fontSize: 11.5,
  color: DashboardTheme.gray500,
};

export const railBig: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 6,
  marginTop: 2,
};

export const railBigValue: CSSProperties = {
  fontFamily: StyleConstants.HEADER_FONT_FAMILY,
  fontSize: 26,
  fontWeight: 700,
  lineHeight: 1.1,
  color: StyleConstants.DARK_BLUE_COLOR,
};

export const railBigUnit: CSSProperties = {
  fontSize: 13,
  color: DashboardTheme.gray500,
};

export const railValue: CSSProperties = {
  display: 'block',
  fontSize: 13.5,
  fontWeight: 600,
  color: StyleConstants.DARK_BLUE_COLOR,
  marginTop: 2,
};

export const railTags: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginTop: 8,
};

export const railSaveBlock: CSSProperties = {
  paddingTop: 16,
  borderTop: `1px solid ${DashboardTheme.gray200}`,
  display: 'grid',
  gap: 8,
  justifyItems: 'stretch',
};

// Destructive action, parked under Save in the rail: quiet until hovered, never competing with
// the primary button.
export const railDeleteButton: NestedCSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  width: '100%',
  marginTop: 4,
  padding: '8px 12px',
  border: 0,
  borderRadius: 8,
  background: 'none',
  color: StyleConstants.PINK_COLOR,
  fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  $nest: {
    '&:hover': {
      background: tint(StyleConstants.PINK_COLOR, 0.08),
    },
  },
};

export const railSaveState: CSSProperties = {
  fontSize: 12,
  color: StyleConstants.PINK_COLOR,
  fontWeight: 600,
};

export const railSaveStateIdle: CSSProperties = {
  fontSize: 12,
  color: DashboardTheme.gray500,
};
